import { Flex, Select } from "antd";
import { useCallback } from "react";

import { BaseModal } from "@/components/BaseModal";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { CameraConfig, updateCameraConfig } from "@/store/slices/config";

interface CameraConfigModalProps {
    open: boolean;
    onClose: () => void;
    onQualityChange?: () => void;
}

const modeOptions = [
    { value: "captureImage", label: "Якість" },
    { value: "canvas", label: "Сумісність" },
] satisfies { value: CameraConfig["mode"]; label: string }[];

const qualityOptions = [
    { value: "4k", label: "4K" },
    { value: "fullHd", label: "Full HD" },
] satisfies { value: CameraConfig["quality"]; label: string }[];

const booleanOptions = [
    { value: true, label: "Увімкнено" },
    { value: false, label: "Вимкнено" },
] satisfies { value: boolean; label: string }[];

export const CameraConfigModal = ({
    open,
    onClose,
    onQualityChange,
}: CameraConfigModalProps) => {
    const dispatch = useAppDispatch();
    const { cameraConfig } = useAppSelector((s) => s.config);

    const handleUpdateConfig = useCallback(
        <K extends keyof CameraConfig>(key: K, value: CameraConfig[K]) => {
            dispatch(
                updateCameraConfig({
                    [key]: value,
                } as Partial<CameraConfig>),
            );
        },
        [dispatch],
    );

    const handleChangeQuality = useCallback(
        (quality: CameraConfig["quality"]) => {
            dispatch(updateCameraConfig({ quality }));
            onQualityChange?.();
        },
        [dispatch, onQualityChange],
    );

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            actions={null}
            title="Налаштування"
            maskClosable
        >
            <Flex vertical gap={12}>
                <Flex vertical gap={4}>
                    <PrimaryText>Режим</PrimaryText>
                    <Select
                        value={cameraConfig.mode}
                        options={modeOptions}
                        onChange={(value) => handleUpdateConfig("mode", value)}
                        size="large"
                    />
                </Flex>

                <Flex vertical gap={4}>
                    <PrimaryText>Якість фото</PrimaryText>
                    <Select
                        value={cameraConfig.quality}
                        options={qualityOptions}
                        onChange={handleChangeQuality}
                        size="large"
                    />
                </Flex>

                <Flex vertical gap={4}>
                    <PrimaryText>Очікування фокусу</PrimaryText>
                    <Select
                        value={cameraConfig.focus}
                        options={booleanOptions}
                        onChange={(value) => handleUpdateConfig("focus", value)}
                        size="large"
                    />
                </Flex>

                <Flex vertical gap={4}>
                    <PrimaryText>Підготовка експозиції</PrimaryText>
                    <Select
                        value={cameraConfig.exposure}
                        options={booleanOptions}
                        onChange={(value) =>
                            handleUpdateConfig("exposure", value)
                        }
                        size="large"
                    />
                </Flex>
            </Flex>
        </BaseModal>
    );
};
